import React, { useEffect, useState } from 'react';
import { Sidebar } from '@/components/Sidebar';
import { api } from '@/utils/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Card } from '@/components/ui/card';
import { Plus, Trash2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const colorOptions = [
  { value: '#10b981', label: 'Verde' },
  { value: '#3b82f6', label: 'Azul' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#f59e0b', label: 'Amarelo' },
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#06b6d4', label: 'Ciano' },
];

export default function TagsPage() {
  const [tags, setTags] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [formData, setFormData] = useState({ name: '', color: '#10b981' });

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const response = await api.getTags();
      setTags(response.data);
    } catch (error) {
      toast.error('Erro ao carregar etiquetas');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createTag(formData);
      toast.success('Etiqueta criada com sucesso!');
      setShowCreateDialog(false);
      setFormData({ name: '', color: '#10b981' });
      fetchTags();
    } catch (error) {
      toast.error('Erro ao criar etiqueta');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir esta etiqueta?')) return;
    try {
      await api.deleteTag(id);
      toast.success('Etiqueta excluída com sucesso!');
      fetchTags();
    } catch (error) {
      toast.error('Erro ao excluir etiqueta');
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Etiquetas
              </h1>
              <p className="text-slate-600" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
                Organize seus leads com etiquetas
              </p>
            </div>

            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="create-tag-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Nova Etiqueta
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Criar Nova Etiqueta</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreate} className="space-y-4">
                  <div>
                    <Label>Nome</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="tag-name-input"
                    />
                  </div>
                  <div>
                    <Label>Cor</Label>
                    <div className="grid grid-cols-3 gap-2 mt-2">
                      {colorOptions.map(option => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setFormData({ ...formData, color: option.value })}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.color === option.value ? 'border-slate-900 shadow-md' : 'border-slate-200'
                          }`}
                          style={{ backgroundColor: option.value }}
                        >
                          <span className="text-white font-medium text-sm">{option.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="submit-tag-button">
                    Criar Etiqueta
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tags.map((tag) => (
              <Card key={tag.id} className="p-6 relative group" data-testid={`tag-card-${tag.id}`}>
                <div className="flex items-center justify-between">
                  <Badge
                    style={{ backgroundColor: tag.color }}
                    className="text-white px-3 py-1 text-sm"
                  >
                    {tag.name}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(tag.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-tag-${tag.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
